import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'

interface UiState {
  sidebarCollapsed: boolean
  theme: Theme
  /**
   * Which legal entity the screens are narrowed to, for a user granted more than one.
   *
   * <p>A display preference, not an authorisation: the server decides what the caller may
   * see from their grants and row-level security enforces it underneath, so choosing an
   * entity here can only ever narrow what was already permitted.
   */
  activeEntityId: string | null
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setTheme: (theme: Theme) => void
  setActiveEntity: (entityId: string | null) => void
}

/**
 * Presentation preferences only — no server data lives here, that is TanStack Query's job.
 * Persisted because a collapsed sidebar snapping back open on every reload is annoying.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      theme: 'system',
      activeEntityId: null,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setTheme: (theme) => {
        set({ theme })
        applyTheme(theme)
      },
      setActiveEntity: (activeEntityId) => set({ activeEntityId }),
    }),
    { name: 'hrms-ui' },
  ),
)

/** Applies the theme to <html>, resolving `system` against the OS preference. */
export function applyTheme(theme: Theme) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const dark = theme === 'dark' || (theme === 'system' && prefersDark)
  document.documentElement.classList.toggle('dark', dark)
}
